import {ChangeDetectionStrategy, Component, computed, effect, Inject, Injectable, Signal, signal} from "@angular/core";
import {DokumentVersjonModell, GruppeId, GruppeMedlemListeModell, OppdaterSkjermingSpesifikasjon, SkjermingDetaljerModell, SkjermingValideringsfeil} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatButton} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {TranslateModule} from "@ngx-translate/core";
import {i18n, I18nTekst, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {VelgMedlemmerKomponent} from "web-ng2/delt/komponenter/skjema/velg-medlemmer/velg-medlemmer";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {EntitetId, TypetValideringsresultatModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {combineLatest, Observable} from "rxjs";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {mapTilData, terminerEtterFørsteRequestRespons} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {SkjermetEntitetType, SkjermingDatatjeneste, SkjermingDetaljerMedValideringsresultatDLM} from "felles-ng2/tjenester/datalager/SkjermingDatatjeneste";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MedlemListevisningKompaktKomponent} from "web-ng2/moduler/medlem/komponenter/medlem-listevisning-kompakt/medlem-listevisning-kompakt";
import {tomSkjermingDetaljerModell} from "felles-ng2/modell/tommeModeller";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {finnMedlemnavn} from "felles-ng2/modell/medlemModellHjelpere";
import {harArvetSkjerming, skjermedeMedlemmerMedArvInfo} from "felles-ng2/modell/skjermingModellHjelpere";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {SkjermingBeskrivelseKomponent} from "web-ng2/moduler/skjerming/komponenter/skjerming-beskrivelse/skjerming-beskrivelse";
import {SkjermingVisKomponent} from "web-ng2/moduler/skjerming/komponenter/skjerming-vis/skjerming-vis";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {ArkivMappenodeDLM} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {skjermetEntitetTypeForDokument} from "felles-ng2/modell/dokumentModellHjelpere";

const debug = initDebugger('SkjermingRedigerDialogKomponent');


/**
 * Dialog for redigering av skjerming.
 */
@Component({
    selector: 'skjerming-dialog',
    templateUrl: 'skjerming-dialog.html',
    styleUrl: 'skjerming-dialog.scss',
    imports: [
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        ReactiveFormsModule,
        SubmitButtonKomponent,
        TranslateModule,
        VelgMedlemmerKomponent,
        MatFormField,
        MatLabel,
        MedlemListevisningKompaktKomponent,
        InlineMeldingKomponent,
        SkjermingBeskrivelseKomponent,
        SkjermingVisKomponent,
        RiktekstRedigeringKomponent,
        MatError,
        StandardValideringsfeilPipe
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkjermingDialogKomponent {

    static vis(dialog: MatDialog, entitetId: EntitetId, type: SkjermetEntitetType, gruppeId: GruppeId) {
        return  SkjermingDialogKomponent.åpneDialog(dialog, 'vis', entitetId, type, gruppeId);
    }

    static rediger(dialog: MatDialog, entitetId: EntitetId, type: SkjermetEntitetType, gruppeId: GruppeId) {
        return  SkjermingDialogKomponent.åpneDialog(dialog, 'rediger', entitetId, type, gruppeId);
    }

    static redigerForArkivmappe(dialog: MatDialog, mappe: ArkivMappenodeDLM) {
       return  SkjermingDialogKomponent.rediger(dialog, mappe.id, SkjermetEntitetType.arkivmappe, mappe.eiendeGruppeId);
    }

    static redigerForDokument(dialog: MatDialog, dokumentversjon: DokumentVersjonModell) {
       return  SkjermingDialogKomponent.rediger(dialog, dokumentversjon.dokumentId, skjermetEntitetTypeForDokument(dokumentversjon)!, dokumentversjon.gruppeId);
    }

    private static åpneDialog(dialog: MatDialog, modus:Modus, entitetId: EntitetId, type: SkjermetEntitetType, gruppeId: GruppeId){
        return dialog.open<SkjermingDialogKomponent, DialogParametre, void>(SkjermingDialogKomponent,
          {
              data: {entitetId, type, gruppeId, modus},
              panelClass: 'skjerming-dialog',
              minWidth: '600px'
          });
    }

    gruppeId: GruppeId;  //settes via parametre
    entitetId: EntitetId; //settes via parametre
    type: SkjermetEntitetType; //settes via parametre

    typer = SkjermetEntitetType;
    typeNavn: string;
    forelderTypeNavn: string;
    skjema = new FormGroup({
        medlemmer: new FormControl<GruppeMedlemListeModell[]>([], {nonNullable: true}),
        aarsak: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenStreng})
    });
    modus = signal<Modus>('vis');
    /** Skjermingsdetaljer hentet fra server */
    skjerming: Signal<SkjermingDetaljerModell>;
    /** Årsak/kommentar vises kun dersom {@link SkjermingDetaljerModell#aarsak} er ikke-tom */
    visAarsak = computed(() => erIkkeTomStreng(this.skjerming().aarsak));
    /** Liste med medlemmer som har skjermet tilgang, samt informasjon om skjermingen er arvet eller ei */
    skjermedeMedlemmer = computed(() => skjermedeMedlemmerMedArvInfo(this.skjerming()));
    /** Liste med aktive medlemmer knyttet til aktuell gruppe */
    gruppemedlemmer = signal<GruppeMedlemListeModell[]>([]);
    /** Liste med medlemmer som kan velges som skjermet, som {@linkn #gruppemedlemmer} men uten kallende brukers medlem-representasjon */
    valgbareMedlemmer = signal<GruppeMedlemListeModell[]>([]);
    /** Liste med medlemmer som skjermet-status ikke kan endres for*/
    laasteMedlemmer = signal<GruppeMedlemListeModell[]>([]);
    typenavn = computed(() => this.enumOversettelser.oversettSkjermetEntitetType(this.type));
    valideringsfeil = signal<string[]>([]);
    lagrer = asynkArbeidPågårSignal();

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                public dialogRef: MatDialogRef<SkjermingDialogKomponent>,
                private avhengigheter: SkjermingDialogKomponentAvhengigheter,
                private oversettelser: Oversettelser,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private enumOversettelser: EnumOversettelser,
                private kontekst: BrukerkontekstTilbyder) {
        this.entitetId = parametre.entitetId;
        this.type = parametre.type;
        this.gruppeId = parametre.gruppeId;

        this.modus.set(parametre.modus);

        const typenavn = this.enumOversettelser.oversettSkjermetEntitetType(this.type);
        this.typeNavn = typenavn.navn;
        this.forelderTypeNavn = typenavn.forelder;
        this.skjerming = toSignal(this.avhengigheter.skjermingsdetaljer(this.entitetId, this.type), {initialValue: tomSkjermingDetaljerModell()});

        if (parametre.modus == 'rediger') {
            this.aktiverRedigeringsmodus();
        }

        effect(() => {
            //dersom bruker ikke har tilgang til skjermet entitet ...
            if (this.skjerming().harSkjermetForelderForKallendeBruker || this.skjerming().erSkjermetForKallendeBruker || this.skjerming().inneholderBarnSkjermetForKallendeBruker)
                this.modus.set('skjermet');
        });
    }

    harArvetSkjerming(medlem: GruppeMedlemListeModell) {
        return harArvetSkjerming(medlem, this.skjerming());
    }

    async lagre() {
        const resultat = await this.lagrer.utfør(() => this.avhengigheter.lagre(this.entitetId, this.type, this.somSpesifikasjon()));
        if (resultat.valideringsresultat.harFeil) {
            this.håndterValideringsfeil(resultat.valideringsresultat);
            return;
        }
        this.meldingTilBruker.infomelding(new I18nTekst(i18n('Tilgangsbegrensninger har blitt oppdatert')));
        this.dialogRef.close(true);
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    //private hjelpemetoder

    private aktiverRedigeringsmodus() {
        this.modus.set('rediger');
        this.initSkjema();
    }

    private initSkjema() {
        combineLatest([toObservable(this.skjerming), this.avhengigheter.gruppeMedlemmer(this.gruppeId)])
          .pipe(takeUntilDestroyed())
          .subscribe(([skjerming, medlemmer]) => this.initSkjemaFraSkjerming(skjerming, medlemmer))
    }

    private initSkjemaFraSkjerming(skjerming: SkjermingDetaljerModell, medlemmer: GruppeMedlemListeModell[]) {
        this.gruppemedlemmer.set(medlemmer);
        const kallendeMedlem = this.kontekst.autentisertBruker().medlemRepresentasjonTilknyttet(this.gruppeId);
        this.valgbareMedlemmer.set(medlemmer.filter(medlem => medlem.id !== kallendeMedlem.id)); //fjerner kallende bruker - skal ikke kunne frata seg selv tilgang
        this.laasteMedlemmer.set(this.valgbareMedlemmer().filter(medlem => skjerming.medlemmerIdUtenTilgangTilForelder.some(skjermet => skjermet === medlem.id)))
        this.skjema.patchValue({
            medlemmer: skjerming.medlemmerUtenTilgang,
            aarsak: skjerming.aarsak
        })
    }

    private somSpesifikasjon(): OppdaterSkjermingSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            medlemmerUtenTilgang: verdier.medlemmer!.map(m => m.id),
            aarsak: verdier.aarsak!
        };
    }
    private håndterValideringsfeil(valideringsresultat: TypetValideringsresultatModell<SkjermingValideringsfeil>) {
        this.valideringsfeil.set(valideringsresultat.feil
          .map(f => {
              switch (f.feilkode) {
                  case SkjermingValideringsfeil.medlem_skal_signere_protokoll:
                      return this.oversettelser.oversett(i18n('{medlem} skal signere protokoll, så tilgang kan ikke stenges'), {medlem: this.finnMedlemNavn(f.data.medlemId)});
                  case SkjermingValideringsfeil.stenge_egen_tilgang:
                      return this.oversettelser.oversett(i18n('{medlem} kan ikke stenge egen tilgang'), {medlem: this.finnMedlemNavn(f.data.medlemId)});
                  case SkjermingValideringsfeil.fjerne_egen_skjerming:
                      return this.oversettelser.oversett(i18n('{medlem} kan ikke gi seg selv tilgang'), {medlem: this.finnMedlemNavn(f.data.medlemId)});
                  case SkjermingValideringsfeil.medlem_skal_signere_dokument:
                      return this.oversettelser.oversett(i18n('{medlem} skal signere et dokument, så tilgang kan ikke stenges'), {medlem: this.finnMedlemNavn(f.data.medlemId)});
                  case SkjermingValideringsfeil.ikke_medlem_i_gruppe: // skal ikke kunne skje i GUI
                  default:
                      return "En feil har oppstått: kode=" + f.feilkode;
              }
          }));
    }

    /** Returnerer navnet til angitt medlem.
     * Returnerer angitt id dersom medlem ikke funnet blandt {@link #gruppemedlemmer} */
    private finnMedlemNavn(medlemId: string): string {
        return finnMedlemnavn(this.valgbareMedlemmer(), medlemId) ?? medlemId;
    }
}

export type Modus = 'vis' | 'rediger' | 'skjermet';

export interface DialogParametre {
    gruppeId: GruppeId;
    entitetId: EntitetId;
    type: SkjermetEntitetType;
    modus: Modus;
}

@Injectable()
export class SkjermingDialogKomponentAvhengigheter {

    constructor(private skjermingDatatjeneste: SkjermingDatatjeneste, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    gruppeMedlemmer(id: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilgjengeligeMedMeta(id)
          .pipe(terminerEtterFørsteRequestRespons(), mapTilData());
    }

    skjermingsdetaljer(id: EntitetId, type: SkjermetEntitetType): Observable<SkjermingDetaljerModell> {
        return this.skjermingDatatjeneste.hentMedMeta(id, type)
          .pipe(terminerEtterFørsteRequestRespons(), mapTilData());
    }

    lagre(id: EntitetId, type: SkjermetEntitetType, spesifikasjon: OppdaterSkjermingSpesifikasjon): Promise<SkjermingDetaljerMedValideringsresultatDLM> {
        return this.skjermingDatatjeneste.lagre(id, type, spesifikasjon);
    }
}
